import { PDFDownloadLink } from "@react-pdf/renderer";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import MShipmentReportPdf from "./MShipmentReportPdf";

export default function MShipmentReport({ shipment = null }) {
  /* ******************** All States ************************* */
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [shipments, setShipments] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [reportShipments, setReportShipments] = useState([]);
  const [reportReady, setReportReady] = useState(false);
  const [licenseInfo, setLicenseInfo] = useState(null);

  /* ******************** All Functions ************************* */

  const toggleId = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const fetchShipments = useCallback(
    async ({ query, page } = {}) => {
      try {
        setLoading(true);
        const usedPage = page || pageNumber;
        const usedQuery = typeof query === "string" ? query : searchTerm;
        const { data } = await API.post(
          "/manufacturer/shipments?page=" + usedPage + "&limit=" + pageLimit,
          {
            query: usedQuery,
          },
          { headers: authHeader() }
        );

        if (data?.success) {
          setShipments(data?.data || []);
          setTotalPages(data?.totalPages || 0);
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to fetch shipments",
              type: "error",
            })
          );
        }
      } catch (err) {
        dispatch(setNotification({ message: err.message, type: "error" }));
      } finally {
        setLoading(false);
      }
    },
    [dispatch, pageLimit, pageNumber, searchTerm]
  );

  const handleGenerateReport = useCallback(async () => {
    const idsToUse = selectedIds.length ? selectedIds : [];

    if (!shipment && !idsToUse.length) {
      dispatch(
        setNotification({
          message: "Please select at least one shipment.",
          type: "error",
        })
      );
      return;
    }

    try {
      setLoading(true);
      setReportReady(false);
      const { data } = await API.post(
        "/manufacturer/shipments-for-report",
        { idArray: idsToUse },
        { headers: authHeader() }
      );

      if (data?.success) {
        setReportShipments(data?.data || []);
        setReportReady(true);
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Failed to generate report data",
            type: "error",
          })
        );
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  }, [dispatch, selectedIds, shipment]);

  const shipmentsForPdf = useMemo(() => {
    if (shipment && shipment.id) return [shipment];
    return reportShipments;
  }, [shipment, reportShipments]);

  useEffect(() => {
    const fetchLicense = async () => {
      try {
        const { data } = await API.get("/manufacturer/efs-license", {
          headers: authHeader(),
        });

        if (data?.success) {
          setLicenseInfo({
            licenseId: data.licenseId,
            limit: data.limit,
            sro: data.sro,
          });
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to fetch EFS License",
              type: "error",
            })
          );
        }
      } catch (err) {
        dispatch(
          setNotification({
            message: err.message,
            type: "error",
          })
        );
      }
    };

    fetchLicense();
  }, [dispatch]);

  /* ******************** All JSX ************************* */

  return (
    <div>
      {/* When shipment is provided directly, skip selection modal */}
      {!shipment && (
        <>
          <button
            type="button"
            className="btn btn-success ms-1"
            onClick={() => {
              setShowModal(true);
              setReportShipments([]);
              setReportReady(false);
              if (!shipments.length) {
                fetchShipments({ query: "", page: 1 });
              }
            }}
          >
            Report
          </button>

          {/* Selection Modal */}
          <Modal
            show={showModal}
            onHide={() => {
              setShowModal(false);
            }}
            size="lg"
          >
            <Modal.Header closeButton>
              <Modal.Title>Select Shipments</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {loading && <Spinner />}
              <form
                className="d-flex mb-2 gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  setPageNumber(1);
                  fetchShipments({ query: searchTerm, page: 1 });
                }}
              >
                <input
                  type="search"
                  className="form-control"
                  placeholder="Search shipments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button className="btn btn-info btn-sm" type="submit">
                  Search
                </button>
              </form>

              <div className="table-responsive" style={{ maxHeight: 300 }}>
                <table className="table table-sm table-bordered mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 30 }} className="text-center">
                        <input
                          type="checkbox"
                          checked={
                            shipments.length > 0 &&
                            shipments.every((s) => selectedIds.includes(s.id))
                          }
                          onChange={() => {
                            const allIdsOnPage = shipments.map((s) => s.id);
                            const allSelectedOnPage =
                              allIdsOnPage.length > 0 &&
                              allIdsOnPage.every((id) =>
                                selectedIds.includes(id)
                              );

                            if (allSelectedOnPage) {
                              // Unselect all on current page
                              setSelectedIds((prev) =>
                                prev.filter((id) => !allIdsOnPage.includes(id))
                              );
                            } else {
                              // Select all on current page (keep previously selected others)
                              setSelectedIds((prev) => [
                                ...prev,
                                ...allIdsOnPage.filter(
                                  (id) => !prev.includes(id)
                                ),
                              ]);
                            }
                          }}
                        />
                      </th>
                      <th>Ship ID</th>
                      <th>B/E No</th>
                      <th>Custom Port</th>
                      <th>B/E DT</th>
                      <th>Expiry Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shipments.length > 0 ? (
                      shipments.map((ship) => {
                        const checked = selectedIds.includes(ship.id);
                        return (
                          <tr key={ship.id}>
                            <td className="text-center align-middle">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleId(ship.id)}
                              />
                            </td>
                            <td className="align-middle">{ship.shipmentId}</td>
                            <td className="align-middle">
                              {ship.b_e_no || "-"}
                            </td>
                            <td className="align-middle">{ship.port || "-"}</td>
                            <td className="align-middle">
                              {ship.b_e_date
                                ? new Date(ship.b_e_date).toLocaleDateString()
                                : "-"}
                            </td>
                            <td className="align-middle">
                              {ship.expiryDate
                                ? new Date(ship.expiryDate).toLocaleDateString()
                                : "-"}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center py-2">
                          No shipments found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-end mt-2 gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    disabled={pageNumber <= 1}
                    onClick={() => {
                      const newPage = pageNumber - 1;
                      setPageNumber(newPage);
                      fetchShipments({ query: searchTerm, page: newPage });
                    }}
                  >
                    Previous
                  </button>
                  <span className="align-self-center small">
                    Page {pageNumber} of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    disabled={pageNumber >= totalPages}
                    onClick={() => {
                      const newPage = pageNumber + 1;
                      setPageNumber(newPage);
                      fetchShipments({ query: searchTerm, page: newPage });
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setShowModal(false)}
              >
                Close
              </button>
              {!reportReady ? (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleGenerateReport}
                  disabled={loading}
                >
                  {loading ? "Generating..." : "Generate PDF"}
                </button>
              ) : (
                <PDFDownloadLink
                  document={
                    <MShipmentReportPdf
                      shipments={reportShipments}
                      licenseInfo={licenseInfo}
                    />
                  }
                  fileName={
                    reportShipments.length === 1
                      ? `shipment-${
                          reportShipments[0].shipmentId || reportShipments[0].id
                        }.pdf`
                      : "shipments-report.pdf"
                  }
                >
                  {({ loading: pdfLoading }) => (
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      disabled={pdfLoading}
                    >
                      {pdfLoading ? "Preparing PDF..." : "Download PDF"}
                    </button>
                  )}
                </PDFDownloadLink>
              )}
            </Modal.Footer>
          </Modal>
        </>
      )}

      {/* Download button only when a shipment is passed directly */}
      {shipment && shipmentsForPdf && shipmentsForPdf.length > 0 && (
        <PDFDownloadLink
          document={
            <MShipmentReportPdf
              shipments={shipmentsForPdf}
              licenseInfo={licenseInfo}
            />
          }
          fileName={
            shipmentsForPdf.length === 1
              ? `shipment-${
                  shipmentsForPdf[0].shipmentId || shipmentsForPdf[0].id
                }.pdf`
              : "shipments-report.pdf"
          }
        >
          {({ loading: pdfLoading }) => (
            <button
              type="button"
              className="btn btn-success btn-sm ms-2"
              disabled={pdfLoading}
            >
              {pdfLoading ? "Preparing PDF..." : "Download PDF"}
            </button>
          )}
        </PDFDownloadLink>
      )}
    </div>
  );
}
